import { LoadingSpinner } from "components/LoadingSpinner";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { Sidebar } from "primereact/sidebar";
import { useEffect, useState } from "react";
import styled from "styled-components";
import {
  IBenefitSelectionExplanation,
  IBenefitSelectionSaveRequest,
  IExplainCheck,
  IExplainCheckBenefit,
  IExplainCheckCandidate,
  IExplainStep,
} from "types/BenefitSelection/BenefitSelection";

import { ExplainCheck } from "./ExplainCheck";
import { Surface } from "./Surface";

const StyledMessage = styled(Message)`
  justify-content: flex-start;
`;

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
  .p-datatable-tbody > tr > td {
    vertical-align: top;
    font-size: 0.85rem;
  }

  /* The step that answered the check being explained. An accent edge rather than a fill: the row
     still has to read as one row of the same table. */
  .p-datatable-tbody > tr.explain-answered > td:first-child {
    border-left: 3px solid var(--primary-color);
  }
  .p-datatable-tbody > tr.explain-answered > td {
    background: var(--surface-50);
  }
`;

const Marker = styled.span`
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--primary-color);
  white-space: nowrap;
`;

const ResultLine = styled.span`
  font-size: 0.85rem;
`;

const Candidates = styled.details`
  font-size: 0.8rem;

  summary {
    cursor: pointer;
    color: var(--text-color-secondary);
  }
`;

const CandidateRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 0.25rem 0;
  border-top: 1px solid var(--surface-200);
`;

// Extracted so `react/no-unstable-nested-components` does not see a component redefined per render.
//
// ⚠️ `kind` IS DELIBERATELY IGNORED. Whether a step lives in code or in this account's saved
// configuration is a fact about who can change it, not about what it does — and a reader asking how
// a copay is chosen was being handed that distinction on every other row.
const looksAtCellBody = (step: IExplainStep) => <span>{step.looksAt}</span>;

const ifFoundCellBody = (step: IExplainStep) => (
  <span>
    {step.ifFound}
    {step.stop ? " Stop." : ""}
  </span>
);

const ifNotCellBody = (step: IExplainStep) => <span>{step.ifNot}</span>;

/** `$225`, or `20%` for the one benefit measured in percent. */
const formatValue = (benefit: string, value: number | null): string => {
  if (value === null) return "blank";
  return benefit === "COINSURANCE_PERCENT" ? `${value}%` : `$${value}`;
};

const candidateValue = (candidate: IExplainCheckCandidate): string => {
  if (candidate.percent !== null) return `${candidate.percent}%`;
  if (candidate.amount !== null) return `$${candidate.amount}`;
  return "no amount";
};

function CandidateLine({ candidate }: { candidate: IExplainCheckCandidate }) {
  const facts = [
    candidate.placeOfService,
    candidate.timePeriodQualifier,
    candidate.coverageLevel,
    candidate.inNetwork ? null : "out of network",
  ].filter(Boolean);

  return (
    <CandidateRow>
      <span className="font-medium">{candidateValue(candidate)}</span>
      <span>{candidate.message.join(" · ") || "no message"}</span>
      {facts.length > 0 && (
        <span className="text-color-secondary">{facts.join(" · ")}</span>
      )}
      {candidate.chosen && (
        <span className="font-medium text-primary">← used</span>
      )}
    </CandidateRow>
  );
}

/**
 * What this check made of one benefit: the value, the server's own reason, and the payer's lines.
 *
 * 🚨 THE LINES ARE COLLAPSED AND THE RESULT IS NOT. Most readings are answered by the result line
 * alone; the lines are what somebody opens when the answer surprises them, and they are long.
 */
function CheckResult({
  benefit,
  result,
}: {
  benefit: string;
  result: IExplainCheckBenefit;
}) {
  return (
    <div
      className="flex flex-column gap-1"
      data-testid={`benefitSelection.explainDrawer.${benefit}.result`}
    >
      <ResultLine>
        <span className="font-medium">
          Result: {formatValue(benefit, result.value)}
        </span>{" "}
        — {result.provenance.reason}
      </ResultLine>

      {result.serviceLine && (
        <Candidates>
          <summary>
            Lines the payer sent on {result.serviceLine} (
            {result.candidates.length})
          </summary>
          {result.candidates.length === 0 ? (
            <CandidateRow>
              <span className="text-color-secondary">
                The payer sent no such line on this service line.
              </span>
            </CandidateRow>
          ) : (
            result.candidates.map((candidate, index) => (
              <CandidateLine
                // The payer's lines have no id of their own and repeat freely — two identical lines
                // are two candidates, so position is the only honest key.
                // eslint-disable-next-line react/no-array-index-key
                key={`${benefit}-candidate-${index}`}
                candidate={candidate}
              />
            ))
          )}
        </Candidates>
      )}
    </div>
  );
}

/**
 * 🚨 THE HIGHLIGHT IS RESOLVED BY ID AGAINST THE TABLE ON SCREEN, NEVER BY POSITION.
 *
 * The server's `stepIndex` counts into the steps array IT built while replaying the check. This
 * drawer renders an explanation it fetched separately, and RTK Query caches it — so the two can be
 * different tables. On dev a payer override saved on the Eligibility page in another tab added an
 * `override-ahead` row to the copay table between the two fetches, and the server's index 3 landed on
 * this table's row 4: the described-but-silent zero marked as the step that produced a value the
 * engine had read off a line.
 *
 * An id cannot slide. It matches exactly one row on screen, or the table is not the one the answer
 * was explained against — and then nothing is highlighted and the panel says so, which is the only
 * honest third option. Zero matches and several matches are the same situation for this purpose: the
 * panel cannot name a row, so it does not.
 */
const answeredStepIn = (
  steps: IExplainStep[],
  stepId?: string | null
): { step: IExplainStep | null; changed: boolean } => {
  if (stepId === null || stepId === undefined) {
    return { step: null, changed: false };
  }
  const matches = steps.filter((step) => step.id === stepId);
  // A step the SERVER declined to name is handled above; reaching here with no match means the
  // server named one and this table does not have it.
  return matches.length === 1
    ? { step: matches[0], changed: false }
    : { step: null, changed: true };
};

function StepTable({
  steps,
  testId,
  answeredStepId,
}: {
  steps: IExplainStep[];
  testId: string;
  answeredStepId?: string | null;
}) {
  const { step: answered, changed } = answeredStepIn(steps, answeredStepId);

  const indexCellBody = (step: IExplainStep) => (
    <>
      <span className="font-medium">{step.index}</span>
      {answered === step && <Marker>← this step answered</Marker>}
    </>
  );

  return (
    <>
      <StyledDataTable
        value={steps}
        dataKey="index"
        data-testid={testId}
        rowClassName={(step: IExplainStep) =>
          answered === step ? "explain-answered" : ""
        }
      >
        <Column header="" style={{ width: "6rem" }} body={indexCellBody} />
        <Column header="Looks at" body={looksAtCellBody} />
        <Column header="If it finds a value" body={ifFoundCellBody} />
        <Column header="If not" body={ifNotCellBody} />
      </StyledDataTable>
      {changed && (
        <span
          className="text-xs text-color-secondary"
          data-testid={`${testId}.changed`}
        >
          No step is marked: this table has changed since this check was
          explained. Explain the check again to see which step answers now.
        </span>
      )}
    </>
  );
}

interface IExplainDrawerProps {
  visible: boolean;
  onHide: () => void;
  explanation?: IBenefitSelectionExplanation;
  /** One benefit's steps, or null for every benefit. */
  benefit: string | null;
  isLoading: boolean;
  isError: boolean;
  /** Absent turns the "explain a specific check" form off — the panel is then the plain explanation. */
  accountId?: string;
  /** The configuration on screen while it differs from what is saved. */
  configuration?: IBenefitSelectionSaveRequest;
}

/**
 * CLE-2792 — "How each benefit is selected", rendered from `GET .../benefitSelection/explain`.
 *
 * 🚨 NOTHING HERE IS COMPOSED ON THE CLIENT. Every sentence in these tables comes from the endpoint,
 * which builds them from the account's saved configuration and from the engine's own constants,
 * pinned by a drift test. A screen that assembled its own prose next to the generated version would
 * be a second account of the behaviour, and the hand-written one goes wrong first — which is exactly
 * how a support call ends up with a confident wrong answer.
 *
 * So this component's whole job is layout: sections, tables, and the note lines.
 *
 * 🚨 ONE BENEFIT AT A TIME WHEN ONE WAS ASKED ABOUT. "How it is selected" is asked from a row — the
 * copay, say — and answering with five benefits' worth of steps means the reader has to find the one
 * they asked about inside the answer. "Before any benefit is read" is shown either way, because
 * every benefit below it is conditional on those steps. Opened from a row that names no benefit,
 * the panel shows them all.
 *
 * 🚨 A CHECK BEING EXPLAINED HIGHLIGHTS EXACTLY ONE ROW, OR NONE. The server maps the value's
 * provenance to a step ID and returns null where no step answered or where two could have; this panel
 * then finds the row carrying that id IN THE TABLE IT IS SHOWING. Where the table on screen holds no
 * such row — it was fetched before a configuration change the answer was explained against — nothing
 * is marked and the panel says the table has changed. It never falls back to a likeliest row: a
 * highlight is a claim about what happened to a patient's estimate. See `answeredStepIn`.
 */
export function ExplainDrawer({
  visible,
  onHide,
  explanation,
  benefit,
  isLoading,
  isError,
  accountId,
  configuration,
}: IExplainDrawerProps) {
  const [check, setCheck] = useState<IExplainCheck | undefined>(undefined);

  // The configuration behind the answer changed, so the answer is about a configuration nobody is
  // looking at any more. Dropped rather than left on screen with a caveat: a highlighted row from a
  // superseded run is indistinguishable from a current one.
  useEffect(() => setCheck(undefined), [configuration, explanation]);

  const shown = benefit
    ? (explanation?.benefits ?? []).filter((entry) => entry.benefit === benefit)
    : explanation?.benefits ?? [];

  return (
    <Sidebar
      visible={visible}
      onHide={onHide}
      position="right"
      style={{ width: "min(58rem, 100vw)" }}
      data-testid="benefitSelection.explainDrawer"
    >
      <Surface className="flex flex-column gap-4">
        <div className="flex flex-column gap-1">
          <h3 className="m-0">How each benefit is selected</h3>
          <span className="text-sm text-color-secondary">
            {explanation?.accountName ?? ""}
          </span>
        </div>

        {accountId && (
          <div className="flex flex-column gap-2">
            <span className="font-medium text-sm">
              Explain a specific check
            </span>
            <ExplainCheck
              accountId={accountId}
              configuration={configuration}
              check={check}
              onResult={setCheck}
            />
          </div>
        )}

        {isLoading && <LoadingSpinner />}

        {isError && (
          <StyledMessage
            severity="error"
            data-testid="benefitSelection.explainDrawer.error"
            text="The explanation could not be loaded."
          />
        )}

        {explanation && (
          <>
            {explanation.beforeAnyBenefit.length > 0 && (
              <div className="flex flex-column gap-2">
                <span className="font-medium text-sm">
                  Before any benefit is read{" "}
                  <span className="text-color-secondary font-normal">
                    applies to every field below
                  </span>
                </span>
                <StepTable
                  steps={explanation.beforeAnyBenefit}
                  testId="benefitSelection.explainDrawer.beforeAnyBenefit"
                />
              </div>
            )}

            {shown.map((entry) => {
              const result = check?.benefits[entry.benefit];

              return (
                <div
                  key={entry.benefit}
                  className="flex flex-column gap-2"
                  data-testid={`benefitSelection.explainDrawer.${entry.benefit}`}
                >
                  <span className="font-medium text-sm">
                    {entry.label}{" "}
                    <span className="text-color-secondary font-normal">
                      {entry.headline}
                    </span>
                  </span>
                  <StepTable
                    steps={entry.steps}
                    testId={`benefitSelection.explainDrawer.${entry.benefit}.steps`}
                    answeredStepId={result?.stepId}
                  />
                  {result && (
                    <CheckResult benefit={entry.benefit} result={result} />
                  )}
                  {entry.notes.map((note) => (
                    <span key={note} className="text-xs text-color-secondary">
                      {note}
                    </span>
                  ))}
                </div>
              );
            })}
          </>
        )}
      </Surface>
    </Sidebar>
  );
}
